<template>
  <div>
    <filters
      :highways-catalog="highways"
      :source="expected"
      :default-from-date="fromDate"
      :min-date="minDate"
      :dateFromFilter="dateFromFilter"
      :dateToFilter="dateToFilter"
      :dummyFilter="true"
      :filterByReVi="reportMode && !isExtranet"
      @updatedDateFrom="fromDate = $event"
      @updatedDateTo="toDate = $event"
      @filtered="updateFiltered"
      @selectedRevi="selectedRevi = $event"
      @updatedNotFinished="notFinished = $event"
    ></filters>

    <security-photos
      v-if="showPhotoModal"
      :is-extranet="isExtranet"
      :reasons="photoReasons"
      :realPlan="currentReal"
      @close="showPhotoModal = false"
      @uploadedPhotos="updateRealPlan()"
      @allReasonsUploaded="updateRealStatus"
    ></security-photos>

    <real-form
      v-if="showReal"
      :expected="currentExpected.expected"
      :days-diff="currentExpected.daysDiff"
      @close="showReal = false"
      @saved="sendReal"
    ></real-form>

    <generator-modal
      v-if="showGenerators"
      :realPlan="currentReal"
      :is-extranet="isExtranet"
      @close="showGenerators = false"
    />

    <photo-evidences
      v-if="showEvidences"
      :realPlan="currentReal"
      :is-extranet="isExtranet"
      @uploadedPhotos="updateExpectedPlan()"
      @close="showEvidences = false"
    />

    <binnacle
      :is-extranet="isExtranet"
      v-if="showBinnacle"
      :realPlan="currentReal"
      @close="showBinnacle = false"
    />

    <concepts-modal
      :affected-id="currentReal.planExpected.affectedId"
      v-if="showRealQuantities"
      @close="showRealQuantities = false"
    />

    <template v-if="!loading && expectedFiltered.length">
      <gantt
        :key="(selectedRevi && selectedRevi.id) || -1"
        :expected="expectedFiltered"
        :real="real"
        :title="workplanTitle"
        :readOnly="!!reportMode"
        :isExtranet="!!isExtranet"
        @assistance="checkAssistance"
        @makeReal="makeReal"
        @makeRealLate="makeReal"
        @securityPhotos="viewSecurityPhotos"
        @addTask="generateWorkPlan"
        @evidences="seeEvidences"
        @generators="seeGenerators"
        @binnacle="displayBinnacle"
        @extranetBinnacle="displayBinnacle"
        @end="askToFinishExecution"
        @reject="returnToPT"
        @setRealQuantities="askForRealQuantities"
        v-bind="ganttProps"
      />
      <p class="text-gray text-right">Mostrando {{ expectedFiltered.length }} siniestros.</p>
    </template>

    <loading-content
      v-else
      :empty-label="emptyPlanLabel"
      empty-icon="assignment"
      :loading="loading"
    >
      <slot name="empty-content-action">
        <md-button class="md-success md-raised" @click="generateWorkPlan" v-if="!reportMode">
          <md-icon>build</md-icon>Generar plan.
        </md-button>
      </slot>
    </loading-content>
  </div>
</template>

<script>
import workPlanService from "@/services/workPlan";
import purchaseService from "@/services/purchase/";
import usesAccountInfoMixin from "@/mixins/usesAccountInfo";

import Gantt from "./Gantt";
import SecurityPhotos from "./SecurityPhotosModal";
import RealForm from "./Real";
import PhotoEvidences from "./PhotoEvidencesModal";
import GeneratorModal from "./GeneratorModal";
import Binnacle from "./BinnacleModal";
import Filters from "./Filter";
import ConceptsModal from "./ConceptsModal";

import moment from "moment";
import { debounce } from "lodash";

// const dateFromFilter = dFrom => rS => dFrom ? rS.startDate >= dFrom : true;
// const dateToFilter = dTo => rS => dTo ? rS.startDate <= dTo : true;
const dateFromFilter = (dFrom) => (rS) => true;
const dateToFilter = (dTo) => (rS) => true;

const MY_WORKPLAN_LABEL = "Mi plan de trabajo";
const ALL_USERS_WORKPLAN_LABEL = "Todos los planes de trabajo";
const USER_WORKPLAN_LABEL = "PT de ";

const NO_FINALIZATION_COMMENT = "NO_FINALIZATION_COMMENT";

const DAYS_AGO_30 = moment().subtract(30, "days");

export default {
  name: "WeeklyExecution",

  props: {
    reportMode: {
      type: Boolean,
      default: false,
    },
    fetchSource: {
      type: Object,
      required: false,
      default: () => {},
    },
    ganttProps: {
      type: Object,
      required: false,
    },
    isExtranet: {
      type: Boolean,
      default: false,
    },
    minDate: {
      type: String,
    },
    defaultFromDate: {
      type: String,
      default: DAYS_AGO_30.format("YYYY-MM-DD"),
    },
    highways: {
      type: Array,
      required: false,
    },
  },

  components: {
    Gantt,
    Filters,
    SecurityPhotos,
    RealForm,
    PhotoEvidences,
    Binnacle,
    ConceptsModal,
    GeneratorModal,
  },

  mixins: [usesAccountInfoMixin],

  methods: {
    // *** ALL PLANS
    fetchPlans() {
      let { fetchReal, fetchExpected } = this.fetchSource || {};
      const { fromDate, toDate } = this.fetchFilters;
      const { notFinished } = this;
      const { currentRequest } = this;

      this.loading = true;
      let userId = this.userId;

      if (currentRequest) {
        currentRequest.cancel();
      }

      const dateStart = fromDate
        ? moment(fromDate).format("YYYY-MM-DD")
        : undefined;
      const dateUntil = toDate
        ? moment(toDate).format("YYYY-MM-DD")
        : undefined;

      const cancelToken = this.$http.CancelToken;
      const source = cancelToken.source();
      this.currentRequest = source;

      if (this.userRoleIs("ADM")) {
        userId = null;
      }

      if (this.reportMode) {
        userId = this.selectedRevi && this.selectedRevi.id;
      }

      const p1 = fetchReal || workPlanService.fetchReal;
      const p2 = fetchExpected || workPlanService.fetchExpected;

      return Promise.all([
        p1({
          residentId: userId,
          finished: !notFinished,
          cancelToken: source.token,
          dateStart,
          dateUntil,
        }),

        p2({
          residentId: userId,
          finished: !notFinished,
          cancelToken: source.token,
          dateStart,
          dateUntil,
        }),
      ])
        .then(([real, expected]) => {
          this.real = real;
          this.expected = expected;
        })
        .catch((err) => {
          this.$fireErrorResponse(err);
          this.real = [];
          this.expected = [];
        })
        .finally(() => (this.loading = false));
    },
    refetchExpected(expectedPlanId) {
      const { expectedSource } = this.fetchSource || {};
      const fetchExpected = expectedSource || workPlanService.fetchExpected;
      return fetchExpected({ id: expectedPlanId }).then((expectedPlan) => {
        const index = this.expected.findIndex((r) => r.id === expectedPlanId);

        if (index != -1) {
          this.expected.splice(index, 1, expectedPlan);
        }

        const { currentExpected } = this;
        if (
          currentExpected.expected &&
          currentExpected.expected.id === expectedPlanId
        ) {
          this.currentExpected.expected = expectedPlan;
        }
      });
    },
    refetchReal(realPlanId) {
      const { realSource } = this.fetchSource || {};
      const fetchReal = realSource || workPlanService.fetchReal;
      return fetchReal({ id: realPlanId }).then((realPlan) => {
        const index = this.real.findIndex((r) => r.id === realPlanId);
        if (index != -1) {
          this.real.splice(index, 1, realPlan);
        }
      });
    },

    /// *** Manage Plan Services
    updateExpectedPlan(realPlan) {
      if (!realPlan) {
        realPlan = this.currentReal;
      }
      const {
        planExpected: { id: expectedId },
      } = realPlan;

      if (!realPlan.dayInitiated) {
        this.refetchExpected(expectedId);
      } else {
        this.refetchExpected(expectedId);
      }
    },
    updateRealPlan(realPlan) {
      this.refetchReal(realPlan ? realPlan.id : this.currentReal.id);
    },
    updateRealStatus(status) {
      const currentReal = this.currentReal;
      const rId = currentReal.id;
      const date = currentReal.date;

      const index = this.real.findIndex((r) => r.id == rId);
      const real = this.real[index];
      const newInfo = {
        ...real.photosInfo,
        [date]: status,
      };
      const newReal = {
        ...real,
        photosInfo: newInfo,
      };

      this.real.splice(index, 1, newReal);
    },
    makeReal(expected, daysDiff = 0) {
      this.currentExpected = { expected, daysDiff };
      this.showReal = true;
    },
    sendReal(real) {
      this.showReal = false;
      workPlanService
        .sendReal(real)
        .then((realPlan) => {
          const plan = {
            ...realPlan,
            date: moment(new Date()).format("YYYY-MM-DD"),
          };
          this.real.splice(this.real.length, 0, plan);
          this.$successMessage({
            title: "Reparación iniciada.",
            html: `<strong>${this.currentExpected.expected.sinisterNumber}</strong> ha iniciado Ejecución`,
          });
          this.viewSecurityPhotos(plan);
          this.refetchExpected(this.currentExpected.expected.id);
        })
        .catch(this.$fireErrorResponse);
    },
    viewSecurityPhotos(realPlan) {
      this.currentReal = realPlan;
      this.showPhotoModal = true;
    },
    generateWorkPlan() {
      this.$router.push({
        name: "Plan de Trabajo",
      });
    },
    updateFiltered(expectedFiltered) {
      this.expectedFiltered = expectedFiltered;
    },
    checkAssistance(realPlan) {
      this.currentReal = realPlan;
      this.showAttendance = true;
    },
    seeEvidences(realPlan) {
      this.currentReal = realPlan;
      this.showEvidences = true;
    },
    seeGenerators(realPlan) {
      this.currentReal = realPlan;
      this.showGenerators = true;
    },
    saveAttendance(workersAttendance) {
      this.showAttendance = false;
      workPlanService
        .sendAssistance(workersAttendance)
        .then(() => {
          this.updateRealPlan(this.currentReal);
          this.$successMessage({ title: `Guardado` });
        })
        .catch(this.$fireErrorResponse);
    },
    displayBinnacle(realPlan) {
      this.showBinnacle = true;
      this.currentReal = realPlan;
    },
    executionEndingCheck(expectedPlan) {
      return new Promise((resolve, reject) => {});
    },
    executionEndingComment(expectedPlan) {
      return new Promise((resolve, reject) => {
        const today = moment(new Date());
        if (today.isBefore(moment(expectedPlan.dateObjects.finishDate))) {
          this.$fire({
            title: "Comentario de finalización prematura.",
            input: "text",
          }).then((value) => {
            if (value && value.value) {
              resolve(value.value);
            }
          });
        } else {
          resolve(NO_FINALIZATION_COMMENT);
        }
      });
    },
    askToFinishExecution(realPlan) {
      this.$fire({
        title: "Se finalizara la ejecución",
        text: "Ya no se podrán subir avances",
        icon: "warning",
        showCancelButton: true,
        cancelButtonText: "Cancelar",
      }).then((result) => {
        if (!result.value) {
          return;
        }
        this.checkFilledQuantities(realPlan)
          .then(() => {
            this.executionEndingComment(realPlan.planExpected).then(
              (comment) => {
                this.finishExecution(realPlan, comment);
              }
            );
          })
          .catch(this.$fireErrorResponse);
      });
    },
    checkFilledQuantities(realPlan) {
      const {
        planExpected: { affectedId },
      } = realPlan;
      return new Promise((resolve, reject) =>
        purchaseService
          .fetchQuantification(affectedId)
          .then(({ concepts }) => {
            const repairConcepts = concepts.filter(({ notToDo }) => !notToDo);
            const invalidConcepts = repairConcepts.filter(
              ({ realQuantity }) => realQuantity === 0
            );
            const valid = invalidConcepts.length === 0;
            if (!valid) {
              const invalidConceptsHtml = `<ul>${invalidConcepts.map(
                ({ name }) => `<li>${name}</li>`
              )}</ul>`;
              const html = `Los siguientes conceptos ejecutables tienen cantidad ejecutada en 0.<br/>${invalidConceptsHtml}. <br/> Confirme que desea continuar.`;
              this.$fire({
                icon: "warning",
                title: "Confirme ejecucion de conceptos",
                html,
                type: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Continuar",
                cancelButtonText: "Cancelar",
              }).then((response) => {
                if (response.value) {
                  resolve(valid);
                } else {
                  reject(
                    new Error(
                      "Revise que las cantidades realmente ejecutadas de los conceptos esten llenadas correctamente."
                    )
                  );
                }
              });
            } else {
              resolve(valid);
            }
          })
          .catch((err) => {
            this.$fireErrorResponse(err);
            reject(err);
          })
          .finally(() => (this.loading = false))
      );
    },
    finishExecution(realPlan, comment) {
      const { id: planExpectedId } = realPlan.planExpected;
      const affectedId = realPlan.planExpected.affectedId;
      workPlanService
        .finishExecution(
          {
            affectedId,
            prematureFinalizationComment: comment,
          },
          realPlan.id
        )
        .then((realPlan) => {
          realPlan.finishedByReVi = true;
          const index = this.real.findIndex((r) => r.id == realPlan.id);
          this.real.splice(index, 1, realPlan);
          this.$successMessage({ title: `Ejecución finalizada` });
          this.refetchExpected(planExpectedId);
        })
        .catch(this.$fireErrorResponse);
    },
    returnToPT(task) {
      this.$confirmMessage(
        {
          title: "¿Rechazar plan de trabajo?",
          text: "Esta acción no se podrá revertir",
        },
        () => {
          return workPlanService
            .returnToPT({ affected: task.planExpected.affectedId })
            .then(() => {
              this.$successMessage({
                title: "Plan de trabajo rechazado",
                text: task.planExpected.sinisterNumber,
              });
              this.fetchPlans();
            })
            .catch(this.$fireErrorResponse);
        }
      );
    },
    setReviewed(task) {
      this.$confirmMessage(
        {
          title: "¿Marcar como revisada?",
          text: "Esta acción no se podrá revertir",
        },
        () => {
          return workPlanService
            .setRealExecutedQuantity({ affected: task.planExpected.affectedId })
            .then(() => {
              this.$successMessage({
                title: "Plan de trabajo revisado",
                text: task.planExpected.sinisterNumber,
              });
              this.fetchPlans();
            })
            .catch(this.$fireErrorResponse);
        }
      );
    },
    askForRealQuantities(realPlan) {
      this.currentReal = realPlan;
      this.showRealQuantities = true;
    },
  },

  created() {
    workPlanService
      .fetchPhotoReasons()
      .then((photoReasons) => (this.photoReasons = photoReasons));
  },

  computed: {
    sinistersStripped() {
      const sinisters = this.expectedFiltered || [];
      return sinisters.map((s) => ({ ...s.sinister, affected: s.affected }));
    },
    workplanTitle() {
      const { reportMode, selectedRevi } = this;
      if (reportMode) {
        if (selectedRevi) {
          return `${USER_WORKPLAN_LABEL} ${selectedRevi.name}`;
        }
        return ALL_USERS_WORKPLAN_LABEL;
      }
      return MY_WORKPLAN_LABEL;
    },
    emptyPlanLabel() {
      const label = "No hay nada en el plan de trabajo";
      if (!this.reportMode) {
        return label;
      }
      const { selectedRevi } = this;
      if (selectedRevi) {
        return `${label} de ${selectedRevi.name}`;
      }
      return label;
    },
    fetchFilters() {
      const {
        toDate,
        fromDate,
        userId,
        selectedRevi,
        reportMode,
        notFinished,
      } = this;
      return {
        toDate,
        fromDate,
        userId,
        selectedRevi,
        reportMode,
        notFinished,
      };
    },
  },

  watch: {
    fetchFilters: {
      immediate: true,
      handler: debounce(function () {
        this.fetchPlans();
      }, 500),
    },
  },

  data() {
    return {
      currentRequest: null,
      notFinished: true,
      selectedRevi: null,
      dateToFilter,
      dateFromFilter,
      toDate: null,
      fromDate: this.defaultFromDate,
      showBinnacle: false,
      showEvidences: false,
      showGenerators: false,
      showPhotoModal: false,
      showReal: false,
      showAttendance: false,
      showRealQuantities: false,
      photoReasons: [],
      real: [],
      expected: [],
      expectedFiltered: [],
      sinisters: [],
      loading: null,
      currentExpected: {
        expected: null,
        daysDiff: 0,
      },
      currentReal: null,
    };
  },
};
</script>



// WEBPACK FOOTER //
// src/pages/WorkPlan/WeeklyExecution/Index.vue